import { useCallback, useRef, useState, type RefObject } from 'react';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { useSplitterDrag } from '../../../../layout/hooks/useSplitterDrag';

type UseResultsPaneDividerParams = {
  bottomPaneCollapsed: boolean;
  setBottomPaneCollapsed: (collapsed: boolean) => void;
  /** `feature:splitter_reset` surface; the Query Editor's by default. */
  surface?: string;
  /**
   * Measure the split against this element instead of the returned
   * `containerRef` — for a host whose results pane is not a direct child of a
   * container the hook owns (the Visual Query Editor tab).
   */
  measureRef?: RefObject<HTMLElement | null>;
  /** Called once when a pointer drag ends. */
  onDragEnd?: () => void;
};

/** Real default for the editor/results split (a percent, 0-100). */
export const RESULTS_SPLIT_DEFAULT_PERCENT = 60;
/** Keyboard resize step for the editor/results split, in percentage points (Shift = the big one). */
export const RESULTS_SPLIT_KEY_STEP = 2;
export const RESULTS_SPLIT_KEY_STEP_BIG = 8;
/** Clamp — the editor pane never shrinks past 20% or grows past 80%. */
const MIN_TOP_PERCENT = 20;
const MAX_TOP_PERCENT = 80;

const clampTop = (percent: number): number =>
  Math.max(MIN_TOP_PERCENT, Math.min(MAX_TOP_PERCENT, percent));

/**
 * Resizable editor/results split for a query editor tab: the dragged
 * `topHeight` percentage (clamped 20–80), the container ref the drag math
 * measures against, the divider's separator props, and the fixed layout
 * heights the split style calculations use.
 *
 * Rides the shared `useSplitterDrag` (CH-13 / T5.8b) like every other seam in
 * the app — pointer capture, rAF-batched drag, arrow-key stepping, and
 * double-click reset — so this divider behaves identically to the sidebar,
 * Explorer/Scripts, terminal, and AI-chat splitters instead of being the one
 * mouse-only site. The ratio math is position-based (the pane height tracks
 * the pointer's absolute Y within the container, not a delta from drag
 * start), unchanged from the original inline handler.
 */
export function useResultsPaneDivider({
  bottomPaneCollapsed,
  setBottomPaneCollapsed,
  surface = 'query-editor-results',
  measureRef,
  onDragEnd,
}: UseResultsPaneDividerParams) {
  const { track } = useAnalytics();
  const [topHeight, setTopHeight] = useState(RESULTS_SPLIT_DEFAULT_PERCENT);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartClientYRef = useRef(0);
  const dragContainerRectRef = useRef<{ top: number; height: number } | null>(null);

  const topHeightForClientY = useCallback(
    (clientY: number): number => {
      const rect = dragContainerRectRef.current;
      if (!rect || rect.height === 0) return topHeight;
      return clampTop(((clientY - rect.top) / rect.height) * 100);
    },
    [topHeight]
  );

  const splitter = useSplitterDrag({
    axis: 'y',
    onDrag: (deltaPx) => {
      setTopHeight(topHeightForClientY(dragStartClientYRef.current + deltaPx));
    },
    onDragEnd,
    onReset: () => {
      setTopHeight(RESULTS_SPLIT_DEFAULT_PERCENT);
      if (bottomPaneCollapsed) setBottomPaneCollapsed(false);
      track('feature:splitter_reset', { surface });
    },
    onKeyStep: (direction, big) => {
      if (bottomPaneCollapsed) setBottomPaneCollapsed(false);
      const step = big ? RESULTS_SPLIT_KEY_STEP_BIG : RESULTS_SPLIT_KEY_STEP;
      setTopHeight((current) => clampTop(current + direction * step));
    },
  });

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (bottomPaneCollapsed) setBottomPaneCollapsed(false);
      dragStartClientYRef.current = e.clientY;
      const rect = (measureRef?.current ?? containerRef.current)?.getBoundingClientRect();
      dragContainerRectRef.current = rect ? { top: rect.top, height: rect.height } : null;
      splitter.onPointerDown(e);
    },
    [bottomPaneCollapsed, setBottomPaneCollapsed, splitter, measureRef]
  );

  // Calculate heights for the panels
  const dividerHeight = 6; // Divider height (h-1.5 = 6px)
  const tabsBarHeight = 28; // TabsList height (h-7 = 28px)
  const statusBarHeight = 20; // StatusBar height (20px)

  return {
    topHeight,
    containerRef,
    handlePointerDown,
    handleDoubleClick: splitter.onDoubleClick,
    handleKeyDown: splitter.onKeyDown,
    isDragging: splitter.isDragging,
    dividerHeight,
    tabsBarHeight,
    statusBarHeight,
  };
}
